import React from 'react';
import { AspectRatio } from 'react-aspect-ratio';
import { Link } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';

import styled from '@emotion/styled';

import { OfferCategory, VennueAccessRequestItem, VenueAccessREnum } from '../../../graphql/API';
import LinkProfile from '../../../pages/Collabs/components/LinkProfile';
import Icons from '../../../themes/icons';
import { formatNumber } from '../../../utils/formatter';
import LazyAvatar from '../../Avatar/LazyAvatar/LazyAvatar';
import LoadingButton from '../../Button/LoadingButton';
import InstagramTag from '../../InstagramTag/InstagramTag';
import { OnAction } from '../../Tables/AccessRequest/useAction';
import { buttonLoadingState, mapToActionRequest } from '../../Tables/AccessRequest/utils';
import TikTokTag from '../../TikTokTag/TikTokTag';

type Props = {
  accessRequest: VennueAccessRequestItem;
  fullProfile: boolean;
  loadingState: Record<string, boolean>;
  onAction: OnAction;
  onAccept?: (accessRequest: VennueAccessRequestItem | null) => void;
  onReject?: (accessRequest: VennueAccessRequestItem | null) => void;
  onRejectWithConfirmation?: (accessRequest: VennueAccessRequestItem | null) => void;
  isOnlineBrand?: boolean;
  onAcceptIncentive?: (accessRequest: VennueAccessRequestItem | null) => void;
};

export const AccessRequestUserProfileCard: React.FC<Props> = ({
  accessRequest,
  fullProfile,
  loadingState,
  onAction,
  onAccept,
  onReject,
  onRejectWithConfirmation,
  isOnlineBrand = false,
  onAcceptIncentive,
}) => {
  return (
    <Box display='flex' flexDirection='column'>
      <Box
        component={Link}
        to={`/users/${accessRequest.userID}?offerID=${accessRequest?.offerID}`}
        position={'relative'}
      >
        <AspectRatio>
          <LazyAvatar
            variant='rounded'
            src={String(accessRequest?.user?.picture?.medium)}
            placeholderSrc={String(accessRequest?.user?.picture?.thumbnail)}
          />
        </AspectRatio>
        <RatingContainer rating={accessRequest?.user?.rating || 0}>
          <Icons.StarIcon />
          {!accessRequest?.user?.rating ? (
            <span>no ratings yet</span>
          ) : (
            <span>{(accessRequest?.user?.rating || 0).toFixed(1)} Rating</span>
          )}
        </RatingContainer>
        {(accessRequest?.totalRequested || 0) > 1 && (
          <RequestedContainer>{accessRequest?.totalRequested} Requests</RequestedContainer>
        )}
        {(accessRequest?.incentiveValue || 0) > 0 && (
          <IncentiveContainer>
            <Icons.DollarSignV3Icon />
          </IncentiveContainer>
        )}
      </Box>

      <Typography
        component='div'
        variant='body2'
        fontWeight='bold'
        sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}
      >
        <LinkProfile
          fontSize='17px'
          fontWeight='600'
          url={`/users/${accessRequest.userID}?offerID=${accessRequest?.offerID}`}
          label={`${accessRequest?.user?.firstname} ${accessRequest?.user?.lastname}`}
          hoverTextDecoration='underline'
        />

        <Box
          component='div'
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            fontSize: '15px',
            fontWeight: 'normal',
            marginTop: '5px',
            columnGap: '28px',
          }}
        >
            {accessRequest?.user?.instagramHandle && (
              <Box component={'div'} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icons.InstagramActiveV2Icon style={{ width: 14, height: 14 }} />
                <InstagramTag
                  username={accessRequest?.user?.instagramHandle}
                  textColor="#1E55A1"
                  showAtSign={false}
                  sx={{ fontWeight: 500, fontSize: '15px', ml: 0.2 }}
                />
              </Box>
            )}
            {accessRequest?.user?.tiktokHandle && (
              <Box component={'div'} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icons.TiktokIcon style={{ width: 14, height: 14 }} />
                <TikTokTag
                  tiktokHandle={accessRequest?.user?.tiktokHandle}
                  textColor="#1E55A1"
                  showAtSign={false}
                  sx={{ fontWeight: 500, fontSize: '15px', ml: 0.2 }}
                />
            </Box>
            )}
        </Box>
      </Typography>
      <Box
        component={Link}
        sx={{
          fontSize: '15px',
          color: '#111',
          my: 0.2,
          textDecoration: 'none',
          ':hover': {
            textDecoration: 'underline',
          },
        }}
        to={`/offers/${accessRequest.offerID}`}
      >
        {isOnlineBrand ? 'Requested' : 'Requested access to'} {accessRequest.offer?.title}
      </Box>
      {accessRequest?.shippingAddress && accessRequest?.offer?.category === OfferCategory.product && (
        <Box display='flex' alignItems='center' gap={0.6} my={0.5}>
          <Typography variant='body2' fontSize='15px' fontWeight='normal'>
            Ship to {accessRequest?.shippingAddress?.city},{' '}
            {accessRequest?.shippingAddress?.country}
          </Typography>
        </Box>
      )}
      <Stack spacing={1} direction='row' mt={0.7}>
        <LoadingButton
          fullWidth
          variant='outlined'
          onClick={() => {
            if (onRejectWithConfirmation && accessRequest?.totalRequested && accessRequest?.totalRequested < 2) {
              onRejectWithConfirmation(accessRequest);
            } else {
              if (isOnlineBrand) {
                if (onReject && (accessRequest?.totalRequested || 0) >= 2) {
                  onReject(accessRequest);
                } else {
                  onAction(mapToActionRequest(accessRequest ?? undefined, VenueAccessREnum.declined));
                }
              } else {
                onAction(mapToActionRequest(accessRequest ?? undefined, VenueAccessREnum.declined));
              }
            }
            if (onAccept) onAccept(null);
          }}
          {...buttonLoadingState(loadingState, accessRequest, VenueAccessREnum.declined)}
        >
          Reject
        </LoadingButton>
        <LoadingButton
          fullWidth
          variant='contained'
          onClick={() => {
            if ((accessRequest?.incentiveValue || 0) > 0) {
              if (onAcceptIncentive) onAcceptIncentive(accessRequest);
            } else {
              onAction(mapToActionRequest(accessRequest ?? undefined, VenueAccessREnum.accepted));
              if (onAccept) onAccept(accessRequest);
            }
          }}
          {...buttonLoadingState(loadingState, accessRequest, VenueAccessREnum.accepted)}
        >
          Accept
        </LoadingButton>
      </Stack>
    </Box>
  );
};

const RatingContainer = styled.div<{ rating: number }>`
  position: absolute;
  top: 2%;
  left: 2%;
  border-radius: 24px;
  background: rgba(0, 0, 0, 0.6);

  color: ${(props) =>
    props.rating >= 4 ? '#03BD7E' : props.rating < 4 && props.rating > 0 ? '#FF7282' : '#fff'};

  display: inline-flex;
  padding: 6px 12px;
  align-items: center;
  gap: 8px;

  font-size: 15px;
  font-weight: 500;
`;

const RequestedContainer = styled.div`
  position: absolute;
  top: 2%;
  right: 2%;
  border-radius: 24px;
  background: rgba(0, 0, 0, 0.6);

  color: #fff;

  display: inline-flex;
  padding: 6px 12px;
  align-items: center;
  gap: 8px;

  font-size: 15px;
  font-weight: 500;
`;

const IncentiveContainer = styled.div`
  display: flex;
  width: 38px;
  height: 38px;
  padding: 6px 12px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  position: absolute;
  top: 2%;
  left: 50%;
`;
